import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Pulse,
  PaperPlaneTilt,
  BellRinging,
  Globe,
  Sparkle,
  ImagesSquare,
  CheckCircle,
  WarningCircle,
  CircleNotch,
  ArrowRight,
} from "@phosphor-icons/react";
import {
  loadNotificationsHealth,
  loadPushCoverage,
  loadPublicSiteHealth,
  loadInquiryHealth,
  formatRelativeAge,
} from "@/lib/systemHealth";
import { loadModerationDigest } from "@/lib/photoModeration";
import { ROLES } from "@/lib/roles";

/**
 * /admin/system — read-only operational snapshot.
 *
 * Designed for launch day and ongoing operational hygiene. Every panel
 * pulls from existing tables admin RLS already permits (no new RPCs,
 * no new SECURITY DEFINER surfaces). When a panel can't be answered
 * cleanly from app data — Resend / VAPID env, Railway crons, Supabase
 * image transforms — we surface those as **manual checks** rather than
 * inventing fake health probes.
 */
export default function AdminSystem() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [notifications, push, site, inquiries, moderation] =
        await Promise.all([
          loadNotificationsHealth({ days: 7 }).catch(() => null),
          loadPushCoverage().catch(() => null),
          loadPublicSiteHealth().catch(() => null),
          loadInquiryHealth().catch(() => null),
          loadModerationDigest({ days: 7 }).catch(() => null),
        ]);
      setData({ notifications, push, site, inquiries, moderation });
    } catch (e) {
      setError(e?.message || "Could not load system health");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <div data-testid="admin-system" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-neutral-500">
            Operations
          </p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            System health
          </h1>
          <p className="mt-2 max-w-2xl text-neutral-600">
            A read-only snapshot of the channels and surfaces that keep
            your families informed. Use this to confirm the system is
            healthy and to spot anything that still needs production setup.
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-system-refresh"
          onClick={refresh}
          disabled={loading}
          className="inline-flex h-9 items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
        >
          {loading ? <CircleNotch size={11} weight="bold" className="animate-spin" /> : <Pulse size={11} weight="bold" />}
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      {error && (
        <div data-testid="admin-system-error" className="rounded-2xl border border-[#C6851E]/30 bg-[#C6851E]/5 p-4 text-sm text-neutral-700">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-16">
          <CircleNotch size={28} weight="bold" className="animate-spin text-primary" />
        </div>
      )}

      {data && (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <NotificationsPanel d={data.notifications} />
            <PushPanel d={data.push} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <ModerationPanel d={data.moderation} />
            <PublicSitePanel d={data.site} inquiries={data.inquiries} />
          </div>

          <ProductionChecklist data={data} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panels
// ---------------------------------------------------------------------------

function PanelShell({ icon: Icon, title, subtitle, testId, children, footer }) {
  return (
    <section
      data-testid={testId}
      className="rounded-2xl border border-neutral-200 bg-white p-5"
    >
      <header className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-primary/10 text-primary-hover">
          <Icon size={18} weight="duotone" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-base font-semibold tracking-tight text-neutral-900">
            {title}
          </h2>
          {subtitle && <p className="text-[12px] text-neutral-500">{subtitle}</p>}
        </div>
      </header>
      <div className="mt-4">{children}</div>
      {footer && <div className="mt-4 border-t border-neutral-100 pt-3 text-[11px] text-neutral-500">{footer}</div>}
    </section>
  );
}

function NotificationsPanel({ d }) {
  if (!d) return <PanelShell icon={PaperPlaneTilt} title="Email channel" testId="admin-system-notifications" subtitle="No data yet."><EmptyHint text="Once a notification is logged, you'll see send/failure counts here." /></PanelShell>;

  const sent = d.by_status.sent || 0;
  const failed = d.by_status.failed || 0;
  const total = d.total;
  const successRate = total ? Math.round((sent / total) * 100) : null;

  return (
    <PanelShell
      icon={PaperPlaneTilt}
      title="Email channel"
      subtitle={`Last ${d.days} days · ${total} total send${total === 1 ? "" : "s"}`}
      testId="admin-system-notifications"
      footer={d.last_failure ? `Most recent failure (${d.last_failure.kind}): ${truncate(d.last_failure.error, 140)}` : null}
    >
      <div className="grid grid-cols-3 gap-2">
        <Stat testId="admin-system-notifications-sent"   value={sent}    label="Sent"   tone="ok" />
        <Stat testId="admin-system-notifications-failed" value={failed}  label="Failed" tone={failed > 0 ? "warn" : "muted"} />
        <Stat testId="admin-system-notifications-rate"   value={successRate == null ? "—" : `${successRate}%`} label="Success" tone={successRate == null ? "muted" : successRate >= 90 ? "ok" : "warn"} />
      </div>
      {Object.keys(d.by_kind).length > 0 && (
        <ul data-testid="admin-system-notifications-by-kind" className="mt-3 space-y-1.5">
          {Object.entries(d.by_kind).map(([kind, c]) => (
            <li key={kind} className="flex items-center justify-between text-[12px]">
              <span className="font-mono text-neutral-700">{kind}</span>
              <span className="text-neutral-500">
                {c.sent || 0} sent · {c.failed || 0} failed
              </span>
            </li>
          ))}
        </ul>
      )}
    </PanelShell>
  );
}

function PushPanel({ d }) {
  if (!d) return <PanelShell icon={BellRinging} title="Push opt-in" testId="admin-system-push" subtitle="No data yet."><EmptyHint text="Coverage updates as families and teachers enable message notifications." /></PanelShell>;

  return (
    <PanelShell
      icon={BellRinging}
      title="Push opt-in"
      subtitle="Web Push subscriptions per role"
      testId="admin-system-push"
    >
      <div className="grid grid-cols-3 gap-2">
        <PushStat testId="admin-system-push-parent"  label="Parents"  d={d[ROLES.PARENT]} />
        <PushStat testId="admin-system-push-teacher" label="Teachers" d={d[ROLES.TEACHER]} />
        <PushStat testId="admin-system-push-admin"   label="Admins"   d={d[ROLES.ADMIN]} />
      </div>
    </PanelShell>
  );
}

function PushStat({ label, d, testId }) {
  const ok = d && d.pct >= 50;
  const muted = !d || d.total === 0;
  const tone = muted ? "muted" : ok ? "ok" : "warn";
  return (
    <Stat
      testId={testId}
      value={d ? `${d.pct}%` : "—"}
      label={`${label} · ${d ? `${d.opted_in}/${d.total}` : "—"}`}
      tone={tone}
    />
  );
}

function ModerationPanel({ d }) {
  return (
    <PanelShell
      icon={ImagesSquare}
      title="Moderation pulse"
      subtitle={d ? `Last ${d.days} days` : "No data yet."}
      testId="admin-system-moderation"
    >
      <div className="grid grid-cols-4 gap-2">
        <Stat testId="admin-system-moderation-pending"  value={d?.pending_now ?? "—"} label="Pending"  tone={(d?.pending_now ?? 0) > 0 ? "warn" : "muted"} />
        <Stat testId="admin-system-moderation-reported" value={d?.reported    ?? "—"} label="Reported" tone="muted" />
        <Stat testId="admin-system-moderation-approved" value={d?.approved    ?? "—"} label="Approved" tone="ok" />
        <Stat testId="admin-system-moderation-rejected" value={d?.rejected    ?? "—"} label="Removed"  tone="muted" />
      </div>
      <Link
        to="/admin/photos/moderation"
        data-testid="admin-system-moderation-link"
        className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-primary-hover"
      >
        Open moderation queue <ArrowRight size={11} weight="bold" />
      </Link>
    </PanelShell>
  );
}

function PublicSitePanel({ d, inquiries }) {
  return (
    <PanelShell
      icon={Globe}
      title="Public site & funnel"
      subtitle="Pages, announcements, and inquiry inbox"
      testId="admin-system-site"
      footer={inquiries?.oldest_open_at ? `Oldest open inquiry · ${formatRelativeAge(inquiries.oldest_open_at)}` : null}
    >
      <div className="grid grid-cols-3 gap-2">
        <Stat testId="admin-system-site-pages"
              value={d ? `${d.pages.published}/${d.pages.total}` : "—"}
              label="Pages live"
              tone={d?.pages?.published > 0 ? "ok" : "muted"} />
        <Stat testId="admin-system-site-announcements"
              value={d ? `${d.announcements.published}/${d.announcements.total}` : "—"}
              label="Announcements live"
              tone={d?.announcements?.published > 0 ? "ok" : "muted"} />
        <Stat testId="admin-system-site-inquiries-open"
              value={inquiries ? inquiries.open_count : "—"}
              label="Open inquiries"
              tone={(inquiries?.open_count ?? 0) > 0 ? "warn" : "muted"} />
      </div>
    </PanelShell>
  );
}

// ---------------------------------------------------------------------------
// Production checklist — derived where possible, manual otherwise.
// ---------------------------------------------------------------------------

function ProductionChecklist({ data }) {
  // Derived items — answered live from the data we just loaded.
  const sent = data.notifications?.by_status?.sent || 0;
  const failed = data.notifications?.by_status?.failed || 0;
  const pushTotal =
    (data.push?.[ROLES.PARENT]?.opted_in || 0) +
    (data.push?.[ROLES.TEACHER]?.opted_in || 0) +
    (data.push?.[ROLES.ADMIN]?.opted_in || 0);
  const pagesLive = data.site?.pages?.published || 0;

  const items = [
    {
      key: "email-channel",
      label: "Transactional email producing successes",
      state: sent > 0 ? "ok" : failed > 0 ? "fail" : "pending",
      detail:
        sent > 0
          ? `${sent} successful sends in the last 7 days`
          : failed > 0
          ? `Logged ${failed} failures and 0 successes — check RESEND_API_KEY in Railway`
          : "No sends recorded yet — once Resend is configured, sent counts will appear here",
    },
    {
      key: "push-active",
      label: "At least one device subscribed to Web Push",
      state: pushTotal > 0 ? "ok" : "pending",
      detail:
        pushTotal > 0
          ? `${pushTotal} subscribed device${pushTotal === 1 ? "" : "s"} across all roles`
          : "No subscriptions yet — confirm VAPID keys are set in Railway and ask a teacher to enable notifications on their phone",
    },
    {
      key: "public-site",
      label: "Public site has at least one published page",
      state: pagesLive > 0 ? "ok" : "pending",
      detail:
        pagesLive > 0
          ? `${pagesLive} page${pagesLive === 1 ? "" : "s"} live on /site/<your-slug>`
          : "No published pages yet — head to CMS → Pages to publish your homepage and about page",
    },
    {
      key: "moderation-clear",
      label: "Photo moderation backlog is clear",
      state: (data.moderation?.pending_now || 0) === 0 ? "ok" : "warn",
      detail:
        (data.moderation?.pending_now || 0) === 0
          ? "No flagged photos pending review"
          : `${data.moderation.pending_now} flagged photo${data.moderation.pending_now === 1 ? "" : "s"} waiting on a decision`,
    },
  ];

  // Manual items — the app can't probe these from the browser, so we
  // mark them as **manual confirmation** with a clear explanation.
  const manual = [
    {
      key: "image-transforms",
      label: "Supabase Storage Image Transformations enabled",
      detail:
        "Pro-tier feature on the Supabase project. When ON, all photo thumbnails and CMS images use bandwidth-optimised transforms.",
    },
    {
      key: "cron-event-reminders",
      label: "Railway cron — event reminders (every 12 h)",
      detail:
        "Hits POST /api/cron/send-event-reminders. Without this, RSVP reminders won't go out.",
    },
    {
      key: "cron-photo-recap",
      label: "Railway cron — weekly photo recap (Sun 15:00 UTC)",
      detail:
        "Hits POST /api/cron/send-weekly-photo-recap. Without this, families won't receive Sunday recap emails.",
    },
    {
      key: "cron-moderation-digest",
      label: "Railway cron — Sunday moderation digest (Sun 16:00 UTC)",
      detail:
        "Hits POST /api/cron/send-weekly-moderation-digest. Mirrors the in-app moderation digest by email.",
    },
    {
      key: "search-console",
      label: "Public site registered in Google Search Console",
      detail:
        "Improves discoverability for prospective families. JSON-LD + breadcrumbs are already wired.",
    },
  ];

  return (
    <section
      data-testid="admin-system-checklist"
      className="rounded-2xl border border-neutral-200 bg-white p-5"
    >
      <header className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-primary/10 text-primary-hover">
          <Sparkle size={18} weight="duotone" />
        </span>
        <div>
          <h2 className="font-heading text-base font-semibold tracking-tight text-neutral-900">
            Production checklist
          </h2>
          <p className="text-[12px] text-neutral-500">
            What we can detect live, plus the steps that still need a hands-on confirmation.
          </p>
        </div>
      </header>

      <ol data-testid="admin-system-checklist-derived" className="mt-4 space-y-2">
        {items.map((it) => (
          <ChecklistItem key={it.key} item={it} />
        ))}
      </ol>

      <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.18em] text-neutral-500">
        Manual confirmation
      </p>
      <ol data-testid="admin-system-checklist-manual" className="mt-2 space-y-2">
        {manual.map((it) => (
          <ChecklistItem key={it.key} item={{ ...it, state: "manual" }} />
        ))}
      </ol>
    </section>
  );
}

function ChecklistItem({ item }) {
  const ICON = {
    ok:      { Cmp: CheckCircle,    cls: "text-primary" },
    warn:    { Cmp: WarningCircle,  cls: "text-[#C6851E]" },
    fail:    { Cmp: WarningCircle,  cls: "text-[#C6851E]" },
    pending: { Cmp: CircleNotch,    cls: "text-neutral-400" },
    manual:  { Cmp: Sparkle,        cls: "text-neutral-400" },
  }[item.state] || { Cmp: CircleNotch, cls: "text-neutral-400" };

  const { Cmp, cls } = ICON;
  return (
    <li
      data-testid={`admin-system-checklist-item-${item.key}`}
      data-state={item.state}
      className="flex items-start gap-3 rounded-2xl border border-neutral-100 bg-white p-3"
    >
      <Cmp size={18} weight="duotone" className={`mt-0.5 flex-shrink-0 ${cls}`} />
      <div className="min-w-0 flex-1">
        <p className="font-heading text-sm font-semibold text-neutral-900">{item.label}</p>
        <p className="mt-0.5 text-[12px] leading-relaxed text-neutral-600">{item.detail}</p>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Tiny atoms
// ---------------------------------------------------------------------------

function Stat({ value, label, tone, testId }) {
  const tones = {
    ok:    "border-primary/30 bg-primary/5 text-primary-hover",
    warn:  "border-[#C6851E]/30 bg-[#C6851E]/5 text-[#C6851E]",
    muted: "border-neutral-200 bg-neutral-50 text-neutral-700",
  };
  return (
    <div data-testid={testId} className={`rounded-2xl border px-3 py-2 ${tones[tone] || tones.muted}`}>
      <p className="font-heading text-xl font-semibold leading-none">{value}</p>
      <p className="mt-1 text-[10px] font-bold uppercase tracking-wider opacity-80">
        {label}
      </p>
    </div>
  );
}

function EmptyHint({ text }) {
  return <p className="text-[12px] text-neutral-500">{text}</p>;
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
